import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  X, 
  ShieldCheck, 
  CheckCircle2, 
  RefreshCw, 
  AlertCircle,
  Sparkles
} from "lucide-react";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoogleSignIn: () => Promise<void>;
  onEmailSignIn?: (email: string) => void;
}

export default function SignInModal({ isOpen, onClose, onGoogleSignIn }: SignInModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleGoogleClick = async () => {
    setError("");
    setLoading(true);
    try {
      await onGoogleSignIn();
      setSuccess("Successfully connected with Google!");
      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 1000);
    } catch (err: any) {
      console.error(err);
      let errMsg = err?.message || "Google Single Sign-On failed.";
      if (errMsg.includes("auth/operation-not-allowed") || errMsg.includes("configuration") || errMsg.includes("operation-not-allowed")) {
        errMsg = "Google Sign-In is not enabled. Please enable 'Google' under 'Authentication > Sign-in method' in your Firebase Console!";
      } else if (errMsg.includes("auth/popup-blocked") || errMsg.includes("popup-blocked")) {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          errMsg = "The sign-in popup was blocked by your mobile browser. Tap your browser's settings/URL bar to allow popups for plantidentifer.com, then try again.";
        } else {
          errMsg = "The sign-in popup was blocked by your browser. Please look for the popup-blocked icon in your browser's URL address bar, select 'Always allow popups for plantidentifer.com', and try again.";
        }
      } else if (errMsg.includes("unauthorized-domain") || errMsg.includes("auth/unauthorized-domain")) {
        errMsg = "This domain (plantidentifer.com) is not authorized for Google Sign-In in your Firebase Console. Please add 'plantidentifer.com' to the Authorized Domains list in your Firebase Authentication settings.";
      } else {
        errMsg += " (Tip: Make sure 'Google' provider is enabled in your Firebase Console under Authentication > Sign-in method, and the current domain is listed in Authorized Domains).";
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Ambient Dark Overlay with high-quality backdrop blur */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-md cursor-pointer"
        />

        {/* Core Auth Dialog Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ type: "spring", stiffness: 350, damping: 30 }}
          className="relative w-full bg-white rounded-[32px] overflow-hidden shadow-2xl border border-gray-100 flex flex-col md:flex-row z-10 transition-all duration-300 max-w-4xl"
        >
          {/* Left Column: Visual Branding Sidebar */}
          <div className="hidden md:flex md:w-5/12 bg-gradient-to-br from-green-950 via-[#0d2a1a] to-emerald-950 text-white p-8 relative flex-col justify-between overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(52,211,153,0.18),transparent_60%)] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="space-y-6 relative z-10 flex-1 flex flex-col justify-between">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-400/20 text-emerald-400 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-lg font-black tracking-tight leading-none bg-gradient-to-r from-white to-emerald-300 bg-clip-text text-transparent">Plant Identifier</h4>
                    <p className="text-[10px] text-emerald-400 font-extrabold uppercase tracking-widest leading-none mt-1">Verified Security Gate</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-black tracking-tight leading-tight">
                    Your Botanical Companion
                  </h3>
                  <p className="text-xs text-emerald-100/70 font-medium leading-relaxed font-sans">
                    Unlock professional computer-vision analysis, catalog offline plant discoveries, and obtain target therapeutic care.
                  </p>
                </div>

                {/* Structured Checklists */}
                <div className="space-y-4 pt-3">
                  <div className="flex items-start gap-3">
                    <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-400 mt-0.5">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-white">Full Scanning History</h5>
                      <p className="text-xs text-emerald-100/60 font-medium">Record and update plant collections on secure cloud vaults.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-400 mt-0.5">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-white">Garden Organizer</h5>
                      <p className="text-xs text-emerald-100/60 font-medium">Bookmark medical resources, outdoor guides & details.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-400 mt-0.5">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-white">Pathogen Spotter</h5>
                      <p className="text-xs text-emerald-100/60 font-medium">Get alerts for active mold, rot, & insect invasions.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Badge */}
              <div className="pt-6 border-t border-emerald-800/60 relative z-10">
                <span className="text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-400/20 rounded-full px-2.5 py-1 tracking-widest">
                  Secure OAuth 2.0 Synchronized
                </span>
                <p className="text-[10px] text-emerald-100/40 mt-3 font-medium">
                  We verify botanical profiles in compliance with international guidelines.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Content Area */}
          <div className="w-full md:w-7/12 p-8 flex flex-col justify-between relative bg-white min-h-[450px]">
            {/* Outer Close Button */}
            <button
              onClick={onClose}
              className="absolute top-5 right-5 w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-100 text-gray-400 hover:text-gray-700 flex items-center justify-center cursor-pointer transition-colors z-20"
              aria-label="Close modal"
            >
              <X className="w-4.5 h-4.5" />
            </button>

            {/* Google-Only Content Form Area */}
            <div className="space-y-6 pt-6 flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
              
              {/* Header Title */}
              <div className="text-center space-y-2">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shadow-3xs mb-2">
                  <Sparkles className="w-6 h-6 animate-pulse" />
                </div>
                <h3 className="text-2xl font-[900] text-gray-900 tracking-tight">
                  Welcome to Plant Identifier
                </h3>
                <p className="text-xs text-gray-500 font-semibold leading-relaxed">
                  Sign in instantly with your Google Account to auto-save scan records, favorite plants & care profiles.
                </p>
              </div>

              {/* Alerts Indicator */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-xs font-semibold text-center select-none flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5 rounded-xl text-xs font-semibold text-center select-none flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                  <span>{success}</span>
                </div>
              )}

              {/* Google Sign-in Action Button */}
              <div className="pt-2">
                <button
                  onClick={handleGoogleClick}
                  disabled={loading}
                  type="button"
                  style={{ height: "50px" }}
                  className="w-full bg-white border-2 border-gray-200 hover:border-emerald-500 text-gray-700 hover:text-emerald-900 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all shadow-3xs flex items-center justify-center gap-3 cursor-pointer hover:-translate-y-0.5 duration-150 disabled:opacity-50"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin text-emerald-600 shrink-0" />
                  ) : (
                    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M12 5.04c1.65 0 3.13.56 4.31 1.69l3.22-3.22C17.58 1.64 15.01 1 12 1 7.39 1 3.48 3.65 1.55 7.5l3.87 3C6.34 7.6 8.9 5.04 12 5.04z" />
                      <path fill="#4285F4" d="M23.49 12.27c0-.81-.07-1.59-.2-2.34H12v4.43h6.46c-.28 1.48-1.12 2.73-2.38 3.58l3.7 2.87c2.16-1.99 3.41-4.91 3.41-8.54z" />
                      <path fill="#FBBC05" d="M5.42 14.5c-.24-.71-.38-1.47-.38-2.5s.14-1.79.38-2.5L1.55 6.5C.56 8.41 0 10.63 0 13c0 2.37.56 4.59 1.55 6.5l3.87-3z" />
                      <path fill="#34A853" d="M12 23c3.24 0 5.96-1.07 7.95-2.91l-3.7-2.87c-1.03.69-2.34 1.1-4.25 1.1-3.1 0-5.66-2.56-6.58-5.46l-3.87 3C3.48 20.35 7.39 23 12 23z" />
                    </svg>
                  )}
                  <span>{loading ? "Connecting to Google..." : "Continue with Google"}</span>
                </button>
              </div>

              {/* Trust disclaimer */}
              <p className="text-[10px] text-gray-400 font-medium text-center leading-relaxed">
                We strictly protect your personal botanical credentials. Your scanning data remains confidential and secure.
              </p>

            </div>

            {/* Compliance & Policy footer details */}
            <div className="mt-8 pt-4 border-t border-gray-100 flex flex-col gap-3">
              <p className="text-[10px] text-gray-400 font-medium leading-relaxed select-none text-left">
                By signing in or creating an account, you affirm guidelines on botanic preservation and encrypted synced security files.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
